import { useEffect, useCallback, useRef, useState } from 'react'
import { logger } from '@/shared/lib/logger'
import { useOrchestrationStore } from '../store/orchestrationStore'
import { getAuthHeader } from '@/shared/lib/auth'
import { createHeaders } from '@/shared/lib/api/base'
import type { SessionEvent } from '../types'
import type { GlobalEventEnvelope } from '@/shared/lib/api/sse'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001'

interface HistoryEntry {
  id: number
  created_at: string
  action: string
  summary: string
  details: Record<string, unknown> | null
}

interface HistoryResponse {
  count: number
  entries: HistoryEntry[]
}

/**
 * Hook to manage orchestrator sessions.
 *
 * - Fetches history from /orchestrator/history on mount
 * - nudge() POSTs to the trigger endpoint (fire-and-forget); events arrive via global bus
 * - running state is set on 202 response and cleared when 'done'/'complete' arrives
 */
export function useOrchestratorStream() {
  const {
    mergeOrchestratorEvents,
    setOrchestratorError,
    setOrchestratorLoading,
    orchestratorStatus,
  } = useOrchestrationStore()

  const [running, setRunning] = useState(false)
  const historyFetchedRef = useRef(false)

  // Fetch history on mount
  useEffect(() => {
    if (historyFetchedRef.current) return
    historyFetchedRef.current = true

    const fetchHistory = async () => {
      setOrchestratorLoading(true)
      try {
        const response = await fetch(`${API_URL}/orchestrator/history?limit=50`, {
          headers: getAuthHeader(),
        })

        if (!response.ok) {
          throw new Error(`History fetch failed: ${response.status}`)
        }

        const data: HistoryResponse = await response.json()

        const events: SessionEvent[] = data.entries.flatMap((entry) => {
          const sessionId = (entry.details?.session_id as string) || `history-${entry.id}`

          const mainEvent: SessionEvent = {
            id: `history-${entry.id}`,
            session_id: sessionId,
            timestamp: entry.created_at,
            type: mapActionToEventType(entry.action),
            text: entry.summary,
            workflow_id: (entry.details?.workflow_id as number) ?? null,
            action_taken: entry.action,
          }

          const actionsTaken = (entry.details?.actions_taken as Array<{ tool: string; input: Record<string, unknown> }>) ?? []
          const actionEvents: SessionEvent[] = actionsTaken.map((act, idx) => ({
            id: `history-${entry.id}-action-${idx}`,
            session_id: sessionId,
            timestamp: entry.created_at,
            type: 'action' as const,
            text: `Called ${act.tool}`,
            workflow_id: (act.input?.workflow_id as number) ?? null,
            action_taken: act.tool,
            tool_input: act.input ?? null,
          }))

          const contextEvents: SessionEvent[] = []
          if (entry.details?.system_prompt) {
            contextEvents.push({
              id: `history-${entry.id}-system-prompt`,
              session_id: sessionId,
              timestamp: entry.created_at,
              type: 'context' as const,
              text: entry.details.system_prompt as string,
              workflow_id: null,
              action_taken: 'system_prompt',
            })
          }
          if (entry.details?.user_prompt) {
            contextEvents.push({
              id: `history-${entry.id}-user-prompt`,
              session_id: sessionId,
              timestamp: entry.created_at,
              type: 'context' as const,
              text: entry.details.user_prompt as string,
              workflow_id: null,
              action_taken: 'user_prompt',
            })
          }

          return [mainEvent, ...contextEvents, ...actionEvents]
        })

        mergeOrchestratorEvents(events)
      } catch (err) {
        logger.error('Failed to fetch orchestrator history', { error: err })
      } finally {
        setOrchestratorLoading(false)
      }
    }

    fetchHistory()
  }, [mergeOrchestratorEvents, setOrchestratorLoading])

  // Nudge: fire-and-forget POST to the orchestrator trigger endpoint.
  // The actual events arrive via the global SSE event bus (source: 'orchestrator').
  const nudge = useCallback(
    async (trigger: string = 'manual') => {
      if (running) return

      setRunning(true)
      setOrchestratorError(null)

      try {
        const response = await fetch(`${API_URL}/orchestrator/stream`, {
          method: 'POST',
          headers: createHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ trigger }),
        })

        if (!response.ok) {
          throw new Error(`Orchestrator trigger failed: ${response.status}`)
        }

        // 202 accepted — events will arrive via global bus
        // running stays true until dispatchOrchestratorEvent receives 'complete'/'error'
      } catch (err) {
        logger.error('Failed to start orchestrator session', { error: err })
        setOrchestratorError('Failed to connect')
        setRunning(false)
      }
    },
    [running, setOrchestratorError]
  )

  // Expose a setter so dispatchOrchestratorEvent (module-level) can clear running state.
  // We do this by storing the setter in a module-level ref.
  useEffect(() => {
    _setRunningRef = setRunning
    return () => {
      if (_setRunningRef === setRunning) _setRunningRef = null
    }
  }, [setRunning])

  return {
    running,
    nudge,
    status: orchestratorStatus,
  }
}

// Module-level ref so dispatchOrchestratorEvent can set running=false when done event arrives.
// Only the most recent mounted useOrchestratorStream instance writes here.
let _setRunningRef: ((v: boolean) => void) | null = null

// ─────────────────────────────────────────────────────────────────────────────
// Global bus dispatcher for orchestrator events
// Called by useGlobalEventStream for envelope.source === 'orchestrator'
// ─────────────────────────────────────────────────────────────────────────────

export function dispatchOrchestratorEvent(envelope: GlobalEventEnvelope): void {
  import('@/features/orchestration/store/orchestrationStore').then(({ useOrchestrationStore }) => {
    const store = useOrchestrationStore.getState()

    const type = envelope.type

    if (type === 'complete' || type === 'error') {
      if (type === 'error') {
        store.setOrchestratorError((envelope.text as string) || 'Session failed')
      }
      _setRunningRef?.(false)
    }

    const sessionEvent = toSessionEvent(envelope)
    if (sessionEvent) {
      store.addOrchestratorEvent(sessionEvent)
    }
  }).catch(() => {})
}

/**
 * Convert a GlobalEventEnvelope to a SessionEvent, or null if not displayable.
 */
function toSessionEvent(raw: Record<string, unknown>): SessionEvent | null {
  const type = raw.type as string
  const displayTypes = ['observation', 'reasoning', 'action', 'waiting', 'error', 'context']
  const mappedType = displayTypes.includes(type) ? (type as SessionEvent['type']) : 'observation'

  const text = (raw.text as string) || ''
  if (!text) return null

  const id = (raw.id as string) || `sse-${Date.now()}-${Math.random()}`
  const session_id = (raw.session_id as string) || id.replace(/_[^_]+$/, '')

  return {
    id,
    session_id,
    timestamp: (raw.timestamp as string) || new Date().toISOString(),
    type: mappedType,
    text,
    workflow_id: (raw.workflow_id as number) ?? null,
    action_taken: (raw.action_taken as string) ?? null,
    tool_input: (raw.tool_input as Record<string, unknown>) ?? null,
  }
}

/**
 * Map backend action strings to event types.
 */
function mapActionToEventType(action: string): SessionEvent['type'] {
  if (action.includes('observe') || action.includes('notice') || action.includes('scan')) {
    return 'observation'
  }
  if (action.includes('think') || action.includes('reason') || action.includes('analyze')) {
    return 'reasoning'
  }
  if (action.includes('act') || action.includes('create') || action.includes('update') || action.includes('reject')) {
    return 'action'
  }
  if (action.includes('wait') || action.includes('escalate') || action.includes('needs_input')) {
    return 'waiting'
  }
  if (action.includes('error')) {
    return 'error'
  }
  return 'observation'
}
